import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircleIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
  LifebuoyIcon,
  ArrowRightIcon,
  ShareIcon,
  DocumentTextIcon,
  SparklesIcon,
  HomeIcon,
  ShieldCheckIcon,
  PhoneIcon,
  EnvelopeIcon,
  CheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const ThankYouPage: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const state = location.state as {
    bookingId: string;
    packageName: string;
    amount: number;
    currency: string;
  } || {};

  const { bookingId, packageName, amount, currency } = state;

  const parsePhone = (raw?: string): { countryCode: string; number: string } => {
    if (!raw) return { countryCode: '+1', number: '' };
    const cleaned = raw.replace(/\s|-/g, '');
    if (cleaned.startsWith('+')) {
      const match = cleaned.match(/^(\+\d{1,3})(\d+)$/);
      if (match) return { countryCode: match[1], number: match[2] };
      return { countryCode: '+1', number: cleaned.replace('+', '') };
    }
    return { countryCode: '+1', number: cleaned };
  };

  useEffect(() => {
    const status = searchParams.get('status');
    const reference = searchParams.get('reference');
    const email = searchParams.get('email');
    const name = searchParams.get('name');
    const qAmount = searchParams.get('amount');
    const qCurrency = searchParams.get('currency');
    const packageId = searchParams.get('packageId');
    const phone = searchParams.get('phone');

    const createBooking = async () => {
      if (!status || !reference || !packageId) return;
      try {
        setCreating(true);
        setCreateError(null);
        const [firstName, ...rest] = (name || '').split(' ');
        const lastName = rest.join(' ') || 'Customer';
        
        console.log('📦 Creating booking from payment...');
        console.log('  Reference:', reference);
        console.log('  Package ID:', packageId);
        console.log('  Email:', email);
        
        const body = {
          firstName: firstName || 'Guest',
          lastName: lastName,
          email: email || '',
          phone: phone || '',
          amount: qAmount ? parseFloat(qAmount) : 0,
          currency: (qCurrency || 'USD').toUpperCase(),
          packageId: packageId,
          transactionId: reference
        };
        
        const res = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bookings/create`, body);
        const created = res.data?.booking;
        
        if (created?.bookingCode) {
          console.log('✅ Booking created:', created.bookingCode);
          // Give a brief moment to show success before redirecting
          setTimeout(() => {
            navigate(`/booking-confirmation/${created.bookingCode}`, { replace: true });
          }, 2000);
        } else {
          console.error('❌ No booking code returned');
          setCreateError('Booking created but confirmation failed. Check your email for details.');
        }
      } catch (err: any) {
        console.error('❌ Booking create failed:', err?.response?.data || err?.message || err);
        setCreateError(err?.response?.data?.message || 'Failed to finalize your booking automatically. Our team will contact you shortly.');
      } finally {
        setCreating(false);
      }
    };

    createBooking();
  }, [navigate, searchParams]);

  return (
    <>
      <Helmet>
        <title>Thank You - Payment Successful | Bali Getaway</title>
        <meta name="description" content="Your Bali getaway voucher has been successfully purchased. Check your email for confirmation details." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-indigo-50 flex items-center justify-center py-16 px-4">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl w-full mx-auto"
        >
          <div className="text-center mb-16">
            {/* Success Icon */}
            <div className="relative inline-block">
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 15 }}
                className="w-32 h-32 bg-gradient-to-br from-green-400 to-emerald-500 rounded-[2.5rem] flex items-center justify-center mx-auto mb-8 shadow-2xl"
              >
                <CheckCircleIcon className="w-16 h-16 text-white" />
              </motion.div>
              <motion.div 
                animate={{ 
                  rotate: [0, 10, -10, 0],
                  scale: [1, 1.1, 1]
                }}
                transition={{ repeat: Infinity, duration: 4 }}
                className="absolute -top-4 -right-4"
              >
                <SparklesIcon className="w-12 h-12 text-yellow-400 fill-current drop-shadow-md" />
              </motion.div>
            </div>

            {/* Main Message */}
            <h1 className="text-5xl lg:text-7xl font-black text-gray-900 mb-6 tracking-tight">
              Payment Successful! 🎉
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed font-medium">
              Thank you for choosing Bali Getaway. Your dream adventure starts now! Your digital voucher is being generated.
            </p>
            <div className="inline-flex items-center px-8 py-3 bg-green-100 text-green-800 rounded-2xl text-lg font-black uppercase tracking-tighter border border-green-200 shadow-sm">
              <ShieldCheckIcon className="w-6 h-6 mr-3" />
              Verified Transaction
            </div>
          </div>

          {/* Booking Details Card */}
          {bookingId && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-[3rem] shadow-2xl overflow-hidden border border-gray-100 mb-12"
            >
              <div className="bg-gradient-to-r from-green-600 to-emerald-700 text-white p-10 flex justify-between items-center">
                <div>
                  <h2 className="text-3xl font-black mb-2 tracking-tight">Booking Summary</h2>
                  <p className="text-green-100 text-lg font-medium opacity-90">Your voucher is now active</p>
                </div>
                <div className="hidden md:block">
                  <DocumentTextIcon className="w-16 h-16 text-white/20" />
                </div>
              </div>

              <div className="p-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-gray-50 rounded-3xl p-8 space-y-4 border border-gray-100">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 font-bold text-xs uppercase tracking-wider">Reference</span>
                      <span className="font-mono text-lg font-black text-green-600 bg-green-50 px-3 py-1 rounded-xl border border-green-100">{bookingId}</span>
                    </div>
                    <div className="flex justify-between items-center py-4 border-y border-gray-200/60">
                      <span className="text-gray-500 font-bold text-xs uppercase tracking-wider">Amount Paid</span>
                      <span className="font-black text-gray-900 text-2xl">{currency} ${amount?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 font-bold text-xs uppercase tracking-wider">Date</span>
                      <span className="font-bold text-gray-900">{new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                    </div>
                  </div>

                  <div className="flex flex-col justify-center space-y-4">
                    <div className="space-y-1">
                      <span className="text-gray-500 font-bold text-xs uppercase tracking-wider">Selected Package</span>
                      <h3 className="font-black text-gray-900 text-xl leading-tight">{packageName}</h3>
                    </div>
                    <div className="flex gap-3">
                      <div className="bg-orange-50 px-4 py-2 rounded-2xl border border-orange-100">
                        <span className="text-[10px] font-black text-orange-400 uppercase block">Validity</span>
                        <span className="font-bold text-orange-700">12 Months</span>
                      </div>
                      <div className="bg-blue-50 px-4 py-2 rounded-2xl border border-blue-100">
                        <span className="text-[10px] font-black text-blue-400 uppercase block">Guests</span>
                        <span className="font-bold text-blue-700">Up to 2 Pers</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Status Overlay */}
          <div className="bg-white rounded-[3rem] p-10 mb-12 shadow-xl border border-gray-100 text-center relative overflow-hidden">
            <AnimatePresence mode="wait">
              {creating ? (
                <motion.div 
                  key="creating"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center py-4"
                >
                  <div className="relative w-16 h-16 mb-6">
                    <div className="absolute inset-0 border-4 border-orange-50 rounded-full"></div>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="absolute inset-0 border-4 border-orange-500 border-t-transparent rounded-full"
                    ></motion.div>
                  </div>
                  <h2 className="text-2xl font-black text-gray-900 mb-2 uppercase tracking-tight">Finalizing Your Booking</h2>
                  <p className="text-gray-500 font-medium">Generating your secure voucher and sending confirmation emails...</p>
                </motion.div>
              ) : createError ? (
                <motion.div 
                  key="error"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="py-4"
                >
                  <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <ExclamationTriangleIcon className="w-8 h-8 text-red-600" />
                  </div>
                  <h2 className="text-2xl font-black text-red-900 mb-2 uppercase tracking-tight">Sync Delayed</h2>
                  <p className="text-red-700 font-medium max-w-md mx-auto">{createError}</p>
                  <p className="text-gray-400 text-sm mt-4 italic">Our support team has been notified and will manually verify your payment.</p>
                </motion.div>
              ) : (
                <motion.div 
                  key="ready"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="py-4"
                >
                  <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <CheckIcon className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-black text-gray-900 mb-2 uppercase tracking-tight">All Set!</h2>
                  <p className="text-gray-500 font-medium">Redirecting you to your interactive booking confirmation...</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* What's Next */}
          <div className="bg-white rounded-[3rem] shadow-xl p-12 mb-12 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gray-50 rounded-full -mr-32 -mt-32 opacity-50"></div>
            <h2 className="text-3xl font-black text-gray-900 mb-10 text-center relative tracking-tight uppercase">What Happens Next?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 relative">
              <motion.div whileHover={{ y: -5 }} className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-lg shadow-blue-200 group-hover:scale-110 transition-transform">
                  <EnvelopeIcon className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-black text-gray-900 mb-3 text-lg uppercase tracking-tighter">Instant Email</h3>
                <p className="text-sm text-gray-500 leading-relaxed font-medium">Detailed confirmation with digital receipt sent within 10 minutes.</p>
              </motion.div>

              <motion.div whileHover={{ y: -5 }} className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-200 group-hover:scale-110 transition-transform">
                  <PhoneIcon className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-black text-gray-900 mb-3 text-lg uppercase tracking-tighter">Expert Call</h3>
                <p className="text-sm text-gray-500 leading-relaxed font-medium">Our travel consultants will call you within 24h to help plan your stay.</p>
              </motion.div>

              <motion.div whileHover={{ y: -5 }} className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-600 rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-lg shadow-purple-200 group-hover:scale-110 transition-transform">
                  <CalendarIcon className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-black text-gray-900 mb-3 text-lg uppercase tracking-tighter">Pick Dates</h3>
                <p className="text-sm text-gray-500 leading-relaxed font-medium">Flexible booking valid for 12 months. Choose your dates at your convenience.</p>
              </motion.div>
            </div>
          </div>

          {/* Support Information */}
          <div className="bg-gradient-to-br from-orange-500 to-yellow-500 rounded-[3rem] p-12 mb-12 text-white shadow-2xl shadow-orange-200 relative overflow-hidden group">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-white/20 via-transparent to-transparent"></div>
            <h3 className="text-2xl font-black mb-8 text-center flex items-center justify-center gap-3 relative tracking-tight">
              <LifebuoyIcon className="w-8 h-8" />
              Need Immediate Help?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative">
              <a href="tel:+1800BALIGET" className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20 hover:bg-white/20 transition-all group text-center">
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <PhoneIcon className="w-6 h-6 text-orange-600" />
                </div>
                <span className="block font-black text-2xl tracking-tight mb-1">+1-800-BALI-GET</span>
                <span className="text-xs font-bold uppercase opacity-80">24/7 Priority Support</span>
              </a>
              
              <a href="mailto:support@baligetaway.com" className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20 hover:bg-white/20 transition-all group text-center">
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <EnvelopeIcon className="w-6 h-6 text-orange-600" />
                </div>
                <span className="block font-black text-xl tracking-tight mb-1 truncate">support@baligetaway.com</span>
                <span className="text-xs font-bold uppercase opacity-80">Response within 2 hours</span>
              </a>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16">
            <motion.button 
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center justify-center px-10 py-5 bg-white text-gray-900 font-black rounded-[2rem] hover:bg-gray-50 transition-all shadow-xl border border-gray-100 uppercase tracking-tight"
            >
              <ArrowDownTrayIcon className="mr-3 w-6 h-6 text-gray-400" />
              DOWNLOAD VOUCHER
            </motion.button>
            
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/booking-lookup"
                className="inline-flex items-center justify-center px-10 py-5 bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-black rounded-[2rem] transition-all shadow-2xl shadow-blue-200 uppercase tracking-tight"
              >
                <CalendarIcon className="mr-3 w-6 h-6" />
                MANAGE BOOKING
              </Link>
            </motion.div>

            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/packages"
                className="inline-flex items-center justify-center px-10 py-5 bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-black rounded-[2rem] transition-all shadow-2xl shadow-orange-200 uppercase tracking-tight"
              >
                <ArrowRightIcon className="mr-3 w-6 h-6" />
                MORE PACKAGES
              </Link>
            </motion.div>
          </div>

          {/* Footer/Travel Tips */}
          <div className="text-center pt-8 border-t border-gray-200/60">
            <p className="text-gray-400 font-black uppercase tracking-widest text-xs mb-8">Traveler Quick Checklist</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {['6+ Month Passport', 'Travel Insurance', 'Visa Review', 'Bali Outfits'].map((tip, idx) => (
                <div key={idx} className="bg-gray-50 px-4 py-2 rounded-xl border border-gray-100 text-[10px] font-black text-gray-500 uppercase tracking-tighter">
                  ✓ {tip}
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default ThankYouPage;
